import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { T, FONT_STACK } from "../customer/customers.theme";
import { CustomersKeyframes } from "../customer/CustomersUI";
import AccountMenu, { LockIcon, PencilIcon, SignOutIcon } from "./AccountMenu";
import EditProfileDialog from "./EditProfileDialog";
import ChangePinDialog from "./ChangePinDialog";

import { logout } from "../../SignupAndLogin/loginSlice";
import { initialsOf, tintFor } from "../../SignupAndLogin/login.theme";
import { readProfiles } from "../../SignupAndLogin/waiterProfiles";
import { businessIdOf } from "../../SignupAndLogin/authenticate";

/**
 * Who is signed in, and what they can do about it.
 *
 * The menu, the profile form and the pin form were all Material-UI, each with
 * its own shadow and type scale — the only things in the app wearing that kit.
 * They are the app's own popover and dialogs now.
 *
 * Change SMS & Phone has been taken out. It is the shop's settings rather than
 * this person's account, it belongs with the other shop settings rather than
 * under an avatar, and it was the third of three entries that all looked like
 * account actions and were not.
 */

const COPY = {
  editProfile: "Edit Profile",
  changePin: "Bedel Pin-ka",
  logout: "Logout",
  waiter: "WAITER",
  profileSaved: "Profile updated",
};

const AppBarFile = (props) => {
  const dispatch = useDispatch();
  const activeUser = useSelector((state) => state.login.activeUser);

  const [menuOpen, setMenuOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [changingPin, setChangingPin] = useState(false);
  const [notice, setNotice] = useState("");

  const avatarRef = React.useRef(null);

  // A waiter is on this machine for one order. Editing a profile or changing a
  // pin are not things to do mid-service, so their menu is Logout and nothing
  // else.
  const isWaiter = activeUser?.role === "waiter";

  // Initials in the signed-in person's own colour, instead of the stock photo
  // that used to sit here. For a waiter it is the SAME colour as their tile on
  // the login screen, so the till looks the same either side of signing in;
  // everyone else falls back to a hash of their username.
  const avatar = React.useMemo(() => {
    const mine = readProfiles(businessIdOf(activeUser)).find(
      (p) => p.id === (activeUser?._id || activeUser?.id)
    );
    return tintFor(mine || { username: activeUser?.username });
  }, [activeUser]);

  /**
   * One action does it all: store.js empties every slice on `login/logout`,
   * so the menu, categories, orders, customers and cart of the business being
   * left behind go with it.
   *
   * This used to clear them one at a time here, and quietly missed several —
   * including `setProductDataFetched(false)`, which was called but never
   * dispatched, so the next business's menu was never even fetched.
   */
  const logoutHandler = () => {
    dispatch(logout());
    props.setNavigation();
  };

  /** A line that fades itself, the same one Administration uses. */
  const say = React.useCallback((message) => {
    setNotice(message);
  }, []);

  React.useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 3000);
    return () => clearTimeout(timer);
  }, [notice]);

  const items = [];

  if (!isWaiter) {
    items.push({
      label: COPY.editProfile,
      icon: <PencilIcon />,
      onClick: () => setEditingProfile(true),
    });
    items.push({
      label: COPY.changePin,
      icon: <LockIcon />,
      onClick: () => setChangingPin(true),
    });
    items.push({ divider: true });
  }

  items.push({
    label: COPY.logout,
    icon: <SignOutIcon />,
    danger: true,
    onClick: logoutHandler,
  });

  return (
    <>
      <CustomersKeyframes />

      {editingProfile ? (
        <EditProfileDialog
          user={activeUser}
          onClose={() => setEditingProfile(false)}
          onSaved={() => say(COPY.profileSaved)}
        />
      ) : null}

      {changingPin ? (
        <ChangePinDialog
          user={activeUser}
          onClose={() => setChangingPin(false)}
          onSaved={say}
        />
      ) : null}

      <div
        style={{
          marginRight: "2%",
          display: "flex",
          alignItems: "center",
          fontFamily: FONT_STACK,
        }}
      >
        {isWaiter && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              height: "26px",
              padding: "0 10px",
              marginRight: "12px",
              background: T.selectedBg,
              border: `1px solid ${T.selectedBorder}`,
              borderRadius: T.radiusPill,
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: T.radiusPill,
                background: T.brand,
              }}
            />
            <span
              style={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.3px",
                color: T.brandInk,
              }}
            >
              {COPY.waiter}
            </span>
          </div>
        )}

        {/* The menu hangs off this wrapper, which is what pins it directly
            under the avatar wherever the bar happens to sit. */}
        <div style={{ position: "relative" }}>
          {/* The name and the avatar are one control: the whole thing is the
              target, which is a bigger thing to hit than a 34px circle. */}
          <button
            ref={avatarRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              height: "44px",
              padding: "0 6px 0 12px",
              background: menuOpen ? T.hover : "transparent",
              border: "none",
              borderRadius: T.radiusPill,
              font: "inherit",
              fontFamily: FONT_STACK,
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: "14px", fontWeight: 500, color: T.ink }}>
              {activeUser ? activeUser.name : ""}
            </span>

            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "34px",
                height: "34px",
                flexShrink: 0,
                background: avatar.bg,
                borderRadius: T.radiusPill,
                fontSize: "13px",
                fontWeight: 700,
                color: avatar.ink,
              }}
            >
              {initialsOf(activeUser?.name || activeUser?.username)}
            </span>
          </button>

          {menuOpen ? (
            <AccountMenu
              anchorRef={avatarRef}
              items={items}
              onClose={() => setMenuOpen(false)}
            />
          ) : null}
        </div>
      </div>

      {notice ? (
        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: "24px",
            transform: "translateX(-50%)",
            zIndex: 1500,
            padding: "11px 18px",
            background: T.ink,
            borderRadius: T.radiusPill,
            fontFamily: FONT_STACK,
            fontSize: "13px",
            fontWeight: 600,
            color: T.surface,
            boxShadow: T.dialog,
          }}
        >
          {notice}
        </div>
      ) : null}
    </>
  );
};

export default AppBarFile;
